import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { ThemeProvider } from '@material-ui/core/styles/';
import { CssBaseline } from '@material-ui/core/';
import { theme } from './App.styles';
import LoginPage from './pages/LoginPage';
import HeaderComponent from './assets/components/Header/Header';
import NavBar from './assets/components/Navbar/Navbar';
import NotFound from './pages/NotFound';
import LoginCallbackError from './pages/LoginCallbackError';
import MaintenancePage from './pages/MaintenancePage';
import Footer from './assets/components/Footer/Footer';
import AssistantChat from './assets/components/AssistantChat/AssistantChat';
import RouteFallback from './assets/components/BlurSkeleton/RouteFallback';
import { IUser } from './interfaces/IUser';
import oktaAuth from './helpers/okta.provider';
import { getNextAvailableDate } from './helpers/getNextAvailableDate';
import { I18nProvider } from './i18n';
import { forcedLanguageFor } from './i18n/spanishOnly';
import { useTalentAccess } from './helpers/useTalentAccess';

/**
 * Freelancers sign in with their VML address only to fill in their skills.
 * For them the whole app is that form: no map, no reservations, no profile.
 *
 * Renders the normal app while the check is in flight rather than a spinner,
 * so nobody else waits on it; a freelancer may see the shell for a moment
 * before it is replaced. Signed-out routes are untouched — the query does
 * not run until there is a session.
 */
/**
 * Deskbooking — desks, lunch, the waitlist, your reservations — is the Costa
 * Rica office's. Guatemala uses the app for the Skill Matrix only, so its
 * screens send them there. The server refuses their bookings as well.
 */
const DeskbookingOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { deskbookingEnabled } = useTalentAccess();
  return deskbookingEnabled ? <>{children}</> : <Redirect to="/skills" />;
};

const FormOnlyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { formOnly } = useTalentAccess();
  if (!formOnly) return <>{children}</>;
  return (
    <Suspense fallback={<RouteFallback />}>
      <FreelanceSkillsPage />
    </Suspense>
  );
};

// Lazy-loaded route components (code-splitting)
const MapPage = lazy(() => import('./pages/MapPage'));
const MeetingReservationPage = lazy(() => import('./pages/MeetingReservationPage'));
const CheckInShortcut = lazy(() => import('./pages/CheckInShortcut'));
const CancelShortcut = lazy(() => import('./pages/CancelShortcut'));
const MyReservationsPage = lazy(() => import('./pages/MyReservationsPage'));
const AdditionalWorkspace = lazy(() => import('./pages/AdditionalWorkspace'));
const IsBlockedPage = lazy(() => import('./pages/isBlockedPage'));
const AdminMainPage = lazy(() => import('./adminPages/AdminDashboard'));
const AdminSettingsPage = lazy(() => import('./adminPages/AdminSettings'));
const AssetControlPage = lazy(() => import('./pages/AssetControl/AssetControlPage'));
const AssetFormPage = lazy(() => import('./pages/AssetControl/AssetFormPage'));
const ReportsPage = lazy(() => import('./pages/Reports/ReportsPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfile/UserProfilePage'));
const TalentPage = lazy(() => import('./pages/Talent/TalentPage'));
const FreelanceSkillsPage = lazy(() => import('./pages/Talent/FreelanceSkillsPage'));

// Warm the route chunks once the app is idle. Webpack dedupes these against the
// lazy() imports above, so a tab switch resolves from cache and the Suspense
// fallback never flashes between the old page and the new one.
const prefetchRoutes = (): void => {
  void import('./pages/MyReservationsPage');
  void import('./pages/MeetingReservationPage');
  void import('./pages/UserProfile/UserProfilePage');
  void import('./pages/AssetControl/AssetControlPage');
  void import('./pages/Reports/ReportsPage');
  void import('./pages/Talent/TalentPage');
  void import('./adminPages/AdminDashboard');
};

const defaultUser: IUser = {
  name: '',
  email: '',
  role: '',
  phone: '',
  areaId: 0,
  blocked: 0,
  isActive: true,
  language: null,
};

const CALLBACK_PATH = process.env.REACT_APP_CALLBACK_PATH;

export const UserContext = React.createContext(defaultUser);

function App() {
  const history = useHistory();
  const location = useLocation();
  const restoreOriginalUri = async (_oktaAuth: any, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri, window.location.origin));
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => getNextAvailableDate());
  const [user, setUser] = useState<IUser>({
    name: '',
    email: '',
    role: '',
    phone: '',
    areaId: 0,
    blocked: 0,
    isActive: true,
    language: null,
  });

  useEffect(() => {
    const idle = (window as any).requestIdleCallback;
    const handle = idle
      ? idle(prefetchRoutes, { timeout: 4000 })
      : window.setTimeout(prefetchRoutes, 1500);
    return () => {
      const cancelIdle = (window as any).cancelIdleCallback;
      if (idle && cancelIdle) cancelIdle(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  const navBarClick = () => {
    setMobileMenuOpen((open) => !open);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Maintenance mode bypass: add ?maintenance_bypass=<key> to the URL once per session
  const bypassKey = 'maintenance_bypass';
  const params = new URLSearchParams(window.location.search);
  if (params.get(bypassKey)) {
    sessionStorage.setItem(bypassKey, params.get(bypassKey) || '');
  }
  const isBypassed =
    sessionStorage.getItem(bypassKey) === process.env.REACT_APP_MAINTENANCE_BYPASS_KEY;

  if (process.env.REACT_APP_MAINTENANCE_MODE === 'true' && !isBypassed) {
    return <MaintenancePage />;
  }

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/*
          Language lives above every route so the header, the nav and each page
          switch together. `user` is fetched by NavBar and lifted here, which
          is what lets the account preference win over the browser default.
        */}
        <I18nProvider
          accountLanguage={user.language}
          accountLoaded={Boolean(user.email)}
          forcedLanguage={forcedLanguageFor(location)}
        >
        <FormOnlyGate>
        <Switch>
          <Route exact path="/login">
            <LoginPage />
          </Route>
          <Route
            path={CALLBACK_PATH}
            render={() => <LoginCallback errorComponent={LoginCallbackError} />}
          />
          <SecureRoute exact path="/control-activos/nuevo">
            <Suspense fallback={<RouteFallback />}>
              <AssetFormPage />
            </Suspense>
          </SecureRoute>
          <SecureRoute path="/">
            <div className="app-layout">
              <HeaderComponent isActive={mobileMenuOpen} handleClick={navBarClick} />
              <NavBar
                active={mobileMenuOpen}
                updateIsAdmin={setIsAdmin}
                updateIsBlocked={setIsBlocked}
                setSideMenu={closeMobileMenu}
                updateUser={setUser}
              />
              <div className="main">
                <div className="content">
                  <div className="content-body">
                  <Suspense fallback={<RouteFallback />}>
                  <Switch>
                    <SecureRoute exact path="/">
                      <DeskbookingOnly>
                        <Switch>
                          {isBlocked && <IsBlockedPage></IsBlockedPage>}
                          <MapPage
                            selectedDate={selectedDate}
                          />
                        </Switch>
                      </DeskbookingOnly>
                    </SecureRoute>
                    <SecureRoute exact path="/meetingReservation">
                      <DeskbookingOnly>
                        <Switch>
                          {isBlocked && <IsBlockedPage></IsBlockedPage>}
                          <MeetingReservationPage
                            selectedDate={selectedDate}
                          />
                        </Switch>
                      </DeskbookingOnly>
                    </SecureRoute>
                    <SecureRoute path="/checkin/:reservationID">
                      <DeskbookingOnly>
                        <UserContext.Provider value={user}>
                          <CheckInShortcut />
                        </UserContext.Provider>
                      </DeskbookingOnly>
                    </SecureRoute>
                    <SecureRoute path="/cancel/:reservationID">
                      <DeskbookingOnly>
                        <UserContext.Provider value={user}>
                          <CancelShortcut />
                        </UserContext.Provider>
                      </DeskbookingOnly>
                    </SecureRoute>
                    <SecureRoute path="/checkin-workstation/:workstationId">
                      <div>TODO</div>
                    </SecureRoute>
                    <SecureRoute exact path="/myreservations">
                      <DeskbookingOnly>
                        <MyReservationsPage handleChangeDate={setSelectedDate} />
                      </DeskbookingOnly>
                    </SecureRoute>
                    <SecureRoute exact path="/perfil">
                      <DeskbookingOnly>
                        <UserProfilePage />
                      </DeskbookingOnly>
                    </SecureRoute>
                    {user?.areaId !== 0 && user?.areaId !== 1 && (
                      // TODO: use Context here
                      <SecureRoute exact path="/additionalWorkspace">
                        <DeskbookingOnly>
                          <AdditionalWorkspace user={user} />
                        </DeskbookingOnly>
                      </SecureRoute>
                    )}
                    <SecureRoute path="/control-activos">
                      <AssetControlPage />
                    </SecureRoute>
                    <SecureRoute path="/reportes/:tab?">
                      <ReportsPage />
                    </SecureRoute>
                    {/* Ruta unica: TalentPage decide si mostrar el directorio
                        de admin o el perfil propio segun talentAccess. */}
                    <SecureRoute path="/skills">
                      <TalentPage />
                    </SecureRoute>
                    {isAdmin && (
                      <Switch>
                        <SecureRoute exact path="/admin">
                          <AdminMainPage
                            handleDate={setSelectedDate}
                            currentDate={selectedDate}
                            isLegacyAdmin={user.role === 'Admin'}
                          />
                        </SecureRoute>
                        <SecureRoute exact path="/admin/settings">
                          <AdminSettingsPage />
                        </SecureRoute>
                        <Route path="*">
                          <NotFound fromMain={true} />
                        </Route>
                      </Switch>
                    )}
                    <Route path="*">
                      {user.email === '' ? <RouteFallback /> : <NotFound fromMain={true} />}
                    </Route>
                  </Switch>
                  </Suspense>
                  </div>
                  <Footer />
                </div>
              </div>
              {!isBlocked && <AssistantChat />}
            </div>
          </SecureRoute>
          <Route path="*">
            <NotFound fromMain={false} />
          </Route>
        </Switch>
        </FormOnlyGate>
        </I18nProvider>
      </ThemeProvider>
    </Security>
  );
}

export default App;